import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import DataTable from './DataTable';
import SortableCell from './SortableCell';
import FilterValue from './FilterValue';

const ASCENDING = 'asc';
const DESCENDING = 'desc';

const FilterRequestDataTable = ({
  data,
  currentPage,
  total,
  perPage,
  table,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const sortBy = useMemo(() => searchParams.get('sort_by'), [searchParams]);
  const sortOrder = useMemo(
    () => searchParams.get('sort_order'),
    [searchParams]
  );
  const pageSize = useMemo(
    () => Number(searchParams.get('per_page') ?? perPage),
    [perPage, searchParams]
  );
  const selectedPage = useMemo(
    () => Number(searchParams.get('page') ?? currentPage),
    [currentPage, searchParams]
  );

  const setSearch = useCallback(
    (key, search) => {
      searchParams.set(key, search);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const onSortingByClick = useCallback(
    (newSortBy) => {
      setSearch('sort_by', newSortBy);
      const newSortOrder = sortOrder === ASCENDING ? DESCENDING : ASCENDING;
      setSearch('sort_order', newSortBy === sortBy ? newSortOrder : ASCENDING);
    },
    [setSearch, sortBy, sortOrder]
  );

  const sortedTable = useMemo(
    () =>
      table.map((col) => {
        let label = col.label;
        if (col.sort !== false) {
          label = (
            <SortableCell
              id={col.name}
              label={col.label}
              onClick={() => onSortingByClick(col.name)}
              isAscending={sortOrder === ASCENDING}
              sortBy={sortBy}
            />
          );
        }

        return {
          sortBy: col.sortBy ?? col.name,
          render:
            col.render ??
            ((data) => (
              <FilterValue value={data[col.name]} field={col.name} format={col.format} />
            )),
          ...col,
          label,
        };
      }),
    [table, sortOrder, sortBy, onSortingByClick]
  );

  return (
    <DataTable
      data={data}
      paginator={{
        rowCount: total,
        pageSize,
        setPageSize: (pageSize) => setSearch('per_page', pageSize),
        onPageChange: ({ selected }) => setSearch('page', selected),
        selectedPage,
        initialPage: 1,
      }}
      table={sortedTable}
    />
  );
};

FilterRequestDataTable.propTypes = {
  data: PropTypes.array.isRequired,
  table: PropTypes.array.isRequired,
  currentPage: PropTypes.number,
  total: PropTypes.number,
  perPage: PropTypes.number,
};

export default FilterRequestDataTable;
